import React, { useState, useEffect } from 'react'
import CenterReview from '../../molecules/CenterReview/CenterReview'
import Head from 'react-helmet'
import StarRatings from '../../StarRatings/StarRatings'
import IconMark from '../../IconMark/IconMark'
import IconX from '../../IconX/IconX'
import { parse } from 'date-fns'
import setHtmlElementOverflowHidden from '../../../utils/setHtmlElementOverflowHidden'
import NativeReviewForm from './NativeReviewForm.tsx'
import NativeReviewsList from './NativeReviewsList.jsx'
import Button from '../../atoms/Button/ButtonV2.tsx'
import ProgressBar from './ProgressBar'

const minimumCountDisplayThreshold = 2

export const getOverallStarRating = (data) => {
  return (
    data.reduce((a, b) => {
      return a + b.overall_star
    }, 0) / data.length
  )
}

const NativeReviews = ({ center, nativeReviews = [] }) => {
  const reviewCons = nativeReviews.map((r) => r.cons).flat()
  const reviewConsCount = Array.from(new Set(reviewCons))
    .reduce((acc, rev) => {
      acc.push({
        title: rev,
        count: reviewCons.filter((rev2) => rev2 === rev).length
      })
      return acc
    }, [])
    .filter((item) => item.count >= minimumCountDisplayThreshold)

  const reviewPros = nativeReviews.map((r) => r.pros).flat()
  const reviewProsCount = Array.from(new Set(reviewPros))
    .reduce((acc, reviewTitle) => {
      acc.push({
        title: reviewTitle,
        count: reviewPros.filter((rev) => rev === reviewTitle).length
      })
      return acc
    }, [])
    .filter((item) => item.count >= minimumCountDisplayThreshold)

  const [showTypeform, setShowTypeform] = useState(false)
  const [listCount, setListCount] = useState(5)

  const [cons] = useState(reviewConsCount)
  const [pros] = useState(reviewProsCount)

  const [prosCollapse, setProsCollapse] = useState(true)

  useEffect(() => {
    setHtmlElementOverflowHidden(showTypeform)
  }, [])

  const overall = getOverallStarRating(nativeReviews)
  const overalls = {
    value:
      nativeReviews.reduce((a, b) => {
        return a + b.value_rating
      }, 0) / nativeReviews.length,
    treatment:
      nativeReviews.reduce((a, b) => {
        return a + b.treatment_rating
      }, 0) / nativeReviews.length,
    accommodations:
      nativeReviews.reduce((a, b) => {
        return a + b.accommodation_rating
      }, 0) / nativeReviews.length,
    food:
      nativeReviews.reduce((a, b) => {
        return a + b.food_rating
      }, 0) / nativeReviews.length
  }
  const jsonData = {
    '@context': 'http://schema.org',
    '@type': 'MedicalClinic',
    name: center.title,
    address: {
      '@type': 'PostalAddress',
      streetAddress: center.address
    }
  }

  if (nativeReviews.length > 0) {
    jsonData['aggregateRating'] = {
      '@type': 'AggregateRating',
      ratingValue: `${overall}`,
      ratingCount: `${nativeReviews.length}`
    }
  }

  if (center?.testimonials && center?.testimonials.length > 0) {
    jsonData['review'] = {
      '@type': 'Review',
      name: 'Testimonial',
      author: {
        '@type': 'Person',
        name: center.testimonials[0].name
      },
      reviewBody: center.testimonials[0].content
    }
  }

  const sortedReviews = nativeReviews
    .sort((a, b) => {
      return (
        parse(b?.submit_date, 'MMMM do, yyyy', new Date()).getTime() -
        parse(a?.submit_date, 'MMMM do, yyyy', new Date()).getTime()
      )
    })
    .slice(0, listCount)

  const reviewImage =
    center.featured_image ||
    `https://res.cloudinary.com/rehabpath/image/upload/c_fill,w_50,h_50/${center.slug}-thumb.jpg`

  return (
    <div className="mb-12 md:mb-20">
      <NativeReviewForm
        onClose={() => setShowTypeform(false)}
        isNativeReviewModalVisible={showTypeform}
        title={center.title}
      />
      <div>
        <Head>
          {(nativeReviews.length >= 1 || center?.testimonials) && (
            <script
              className="structured-data-list"
              type="application/ld+json"
            >
              {JSON.stringify(jsonData)}
            </script>
          )}
        </Head>
      </div>
      {nativeReviews.length >= 1 && (
        <div className="mx-auto flex flex-col items-start justify-center md:flex-row">
          <div className="w-full px-4 md:sticky md:top-40 md:mx-0 md:w-[464px] md:min-w-[464px]">
            <div className="mb-6 h-fit w-full rounded-2xl border border-gray-300 bg-gray-50 p-4  md:p-8">
              <div className="mb-8">
                <div className="mb-2 flex">
                  <span className="text-xl font-bold">
                    {overall?.toFixed(1)}
                  </span>

                  <span className="ml-1.5 text-xl font-medium text-gray-800">
                    Average of {nativeReviews.length} review(s)
                  </span>
                </div>

                <div className="inline-block align-top">
                  <StarRatings
                    rating={overall}
                    starRatedColor="#F1C249"
                    starSpacing="5px"
                    numberOfStars={5}
                    uniqueKey="native-reviews"
                    starDimension="26px"
                  />
                </div>
              </div>
              <div>
                <div>
                  <div className="flex flex-col gap-6">
                    <ProgressBar
                      label="Accommodations"
                      value={overalls.accommodations}
                      bgColor="bg-[#F1C249]"
                    />
                    <ProgressBar
                      label="Food & Nutrition"
                      value={overalls.food}
                      bgColor="bg-[#F1C249]"
                    />
                    <ProgressBar
                      label="Treatment"
                      value={overalls.treatment}
                      bgColor="bg-[#F1C249]"
                    />
                    <ProgressBar
                      label="Value"
                      value={overalls.value}
                      bgColor="bg-[#F1C249]"
                    />
                  </div>
                </div>
                {(pros.length > 0 || cons.length > 0) && (
                  <>
                    <div className="my-6 h-px w-full bg-gray-300" />

                    <div className="mb-8 flex flex-wrap gap-4">
                      {pros.length > 0 && (
                        <NativeReviewsList
                          items={pros}
                          title="Pros"
                          Icon={IconMark}
                          isCollapsed={prosCollapse}
                          iconColor="text-green-700"
                        />
                      )}
                      {cons.length > 0 && (
                        <NativeReviewsList
                          items={cons}
                          title="Cons"
                          Icon={IconX}
                          isCollapsed={prosCollapse}
                          iconColor="text-gray-500"
                        />
                      )}
                    </div>
                    {cons.length > 0 && (
                      <p
                        className="cursor-pointer text-sm font-semibold"
                        onClick={() => setProsCollapse(!prosCollapse)}
                      >
                        {prosCollapse ? 'See More' : 'See Less'}
                      </p>
                    )}
                  </>
                )}

                <Button
                  className="w-full"
                  type="tertiary"
                  onClick={() => setShowTypeform(true)}
                >
                  Leave a Review
                </Button>
              </div>
            </div>
          </div>

          <div className="mx-4 p-0 md:ml-8 md:max-w-[744px]">
            {sortedReviews
              .filter((review) => review.review_body)
              .map((review, i) => {
                return (
                  <CenterReview
                    divider={i + 1 !== nativeReviews.length}
                    data={review}
                    image={reviewImage}
                    title={center.title}
                    key={review.id}
                  />
                )
              })}
            {nativeReviews.length > 5 && nativeReviews.length > listCount && (
              <div>
                <button
                  onClick={() => setListCount(listCount + 5)}
                  className="m-auto flex h-[52px] w-full items-center justify-center rounded-lg border 
                  border-gray-400 text-lg text-gray-900 md:w-[211px]"
                >
                  Show More Reviews
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NativeReviews
